import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ScanLine } from "lucide-react";
import { QR_TILES, CATEGORIES } from "@/data/mockData";
import { toast } from "sonner";

const COLOR_MAP = {
  amber: "text-amber-400",
  pink: "text-pink-400",
  purple: "text-purple-400",
  cyan: "text-cyan-400",
  green: "text-green-400",
};

// Deterministic fake QR pattern from an id
function QRPattern({ seed, colorCls }) {
  const cells = [];
  let x = 0;
  for (const c of seed + "appyhour") x = (x * 31 + c.charCodeAt(0)) % 100000;
  for (let i = 0; i < 49; i++) {
    x = (x * 1103515245 + 12345) % 2147483648;
    cells.push((x >> 8) % 2 === 0);
  }
  return (
    <div className="grid grid-cols-7 gap-[3px] w-full aspect-square">
      {cells.map((on, i) => (
        <div key={i} className={`rounded-[2px] ${on ? `bg-current ${colorCls}` : "bg-slate-800"}`} />
      ))}
    </div>
  );
}

export default function QRGrid() {
  const navigate = useNavigate();

  const scan = (tile) => {
    const cat = CATEGORIES.find((c) => c.id === tile.category);
    toast.success(`Scanned ${tile.label} @ ${tile.venue}!`);
    navigate(`/setup?venue=${encodeURIComponent(tile.venue)}&cat=${cat.id}`);
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-2 pt-4">
        <button data-testid="qr-back-btn" onClick={() => navigate("/")} className="text-slate-400 p-1">
          <ArrowLeft strokeWidth={2.5} />
        </button>
        <h1 className="font-display text-3xl font-black text-slate-50">Scan a Table</h1>
      </div>
      <p className="text-slate-400 mb-6 flex items-center gap-2">
        <ScanLine size={16} className="text-amber-400" /> Tap a code to buzz into that table's quiz.
      </p>

      <div className="grid grid-cols-2 gap-4" data-testid="qr-grid">
        {QR_TILES.map((tile, i) => (
          <motion.button
            key={tile.id}
            data-testid={`qr-tile-${tile.id}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.06 }}
            whileTap={{ scale: 0.94 }}
            onClick={() => scan(tile)}
            className="bg-slate-900 rounded-2xl p-4 border border-slate-800 no-tap-highlight"
          >
            <QRPattern seed={tile.id} colorCls={COLOR_MAP[tile.color]} />
            <div className="mt-3 text-left">
              <div className="font-display font-bold text-slate-50">{tile.label}</div>
              <div className="text-slate-500 text-xs truncate">{tile.venue}</div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
